import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Layers, CircuitBoard, Gauge, Wrench, ShieldCheck, Cog, Fuel, Zap,
} from "lucide-react";
import Layout from "@/components/Layout";
import { useI18n } from "@/i18n";
import { brandAssets, stock } from "@/data/brand";
import { services } from "@/data/services";
import { vehicles } from "@/data/vehicles";
import InfiniteMarquee from "@/components/InfiniteMarquee";

export default function Home() {
  const { t, lang } = useI18n();
  const featured = services.filter((s) =>
    ["stage-1-ecu-remap", "stage-2-ecu-remap", "stage-3-ecu-remap", "custom-ecu-calibration", "tcu-dsg-remap", "ecu-diagnostics"].includes(s.slug)
  );
  const whyItems = t("home.why.items");
  const whyIcons = [Layers, CircuitBoard, Gauge, Wrench, ShieldCheck, Cog, Fuel, Zap];

  return (
    <Layout seoTitle={t("home.heroTitle")} seoDescription={t("home.heroSubtitle")}>
      {/* ─── HERO ────────────────────────────────────────────────── */}
      <section data-testid="home-hero" className="relative min-h-[86vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={brandAssets.garage}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover opacity-70"
          />
          {/* Directional gradient — keeps text side dark for readability, reveals workshop on the right */}
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/25" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-transparent to-black/80" />
        </div>
        <div className="container-mt relative z-10 py-16 md:py-20">
          <div className="max-w-4xl fade-in-up">
            <h1 className="h-display text-[3.25rem] leading-[0.95] sm:text-6xl md:text-7xl lg:text-8xl text-white mb-8">
              {lang === "lt" ? (
                <>Galia be <span className="text-brand-lime">kompromisų.</span></>
              ) : (
                <>Performance <span className="text-brand-lime">without compromise.</span></>
              )}
            </h1>
            <p className="text-base md:text-lg text-zinc-200 max-w-xl leading-relaxed mb-10">
              {t("home.heroSubtitle")}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact" data-testid="hero-cta-book" className="btn-primary">
                {t("common.bookConsultation")} <ArrowRight size={15} />
              </Link>
              <Link to="/services" data-testid="hero-cta-services" className="btn-outline">
                {t("common.viewServices")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── WHY MAD TURBO (merged: capabilities + approach) ─────── */}
      <section className="section-y border-t border-white/[0.06]">
        <div className="container-mt grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <h2 className="h-display text-4xl sm:text-5xl md:text-6xl text-white mb-6">
              {t("home.why.title")}
            </h2>
            <p className="text-base md:text-lg text-zinc-300 leading-relaxed max-w-md mb-8">
              {t("home.why.subtitle")}
            </p>
            <img
              src={brandAssets.driftCar}
              alt="MAD TURBO racecar with team livery"
              className="w-full rounded-2xl border border-zinc-800 object-cover aspect-[16/10]"
              loading="lazy"
            />
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
            {whyItems.map((w, i) => {
              const Icon = whyIcons[i % whyIcons.length];
              return (
                <div
                  key={i}
                  data-testid={`why-${i}`}
                  className="card-mt card-mt-pad card-interactive mt-reveal"
                  style={{ transitionDelay: `${i * 40}ms` }}
                >
                  <div className="icon-tile mb-4">
                    <Icon size={18} />
                  </div>
                  <h3 className="text-white font-display font-bold text-xl md:text-2xl uppercase mb-2 tracking-tight">{w.t}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">{w.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── SERVICES PREVIEW ──────────────────────────────────────── */}
      <section className="section-y border-t border-white/[0.06]">
        <div className="container-mt">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
            <div>
              <h2 className="h-display text-4xl sm:text-5xl md:text-6xl text-white mb-5">
                {t("home.servicesPreview.title")}
              </h2>
              <p className="text-base md:text-lg text-zinc-400 leading-relaxed max-w-2xl">
                {t("home.servicesPreview.subtitle")}
              </p>
            </div>
            <Link to="/services" className="btn-outline shrink-0 self-start md:self-end mb-2">
              {t("common.viewServices")} <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {featured.map((s, i) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`home-service-${s.slug}`}
                className="card-mt card-mt-pad card-interactive group mt-reveal flex flex-col"
                style={{ transitionDelay: `${i * 50}ms` }}
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="num-chip">{String(i + 1).padStart(2, "0")}</span>
                  <span className="eyebrow text-[10px]">{t(`services.category${s.category.charAt(0).toUpperCase() + s.category.slice(1)}`)}</span>
                </div>
                <h3 className="h-display text-2xl md:text-[1.65rem] text-white mb-3 leading-tight">
                  {lang === "lt" ? s.title.lt : s.title.en}
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed mb-6 flex-1">
                  {lang === "lt" ? s.short.lt : s.short.en}
                </p>
                <span className="inline-flex items-center gap-2 text-brand-lime font-mono uppercase text-[10px] tracking-[0.22em] mt-auto">
                  {t("common.readMore")}
                  <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ─── BRAND MARQUEE — true infinite, drag/swipe supported ── */}
      <section className="py-10 md:py-14 border-t border-white/[0.06]">
        <InfiniteMarquee
          items={vehicles}
          duration={30}
          renderItem={(v) => (
            <span className="text-2xl md:text-3xl font-display font-bold uppercase tracking-tight text-zinc-600 hover:text-white transition-colors duration-300 whitespace-nowrap">
              {v.name}
            </span>
          )}
        />
      </section>

      {/* ─── FINAL CTA ───────────────────────────────────────────── */}
      <section className="section-y border-t border-white/[0.06]">
        <div className="container-mt">
          <div className="relative overflow-hidden rounded-3xl border border-zinc-800 mt-reveal">
            <img
              src={stock.laptopDiagnostics}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover opacity-50"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/30" />
            <div className="relative z-10 p-8 md:p-14 lg:p-20 grid grid-cols-1 md:grid-cols-5 gap-8 items-center">
              <div className="md:col-span-3">
                <h3 className="h-display text-4xl md:text-5xl lg:text-6xl text-white mb-5">{t("common.ctaTitle")}</h3>
                <p className="text-zinc-200 text-base md:text-lg max-w-xl leading-relaxed">{t("common.ctaText")}</p>
              </div>
              <div className="md:col-span-2 flex flex-col sm:flex-row md:flex-col gap-3 md:items-end">
                <Link to="/contact" className="btn-primary w-full sm:w-auto justify-center">
                  {t("common.bookConsultation")} <ArrowRight size={15} />
                </Link>
                <Link to="/vehicles" className="btn-outline w-full sm:w-auto justify-center">
                  {t("nav.vehicles")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
